/**
 * PATH: packages/ui/src/kanban/components/board-view/CardFacePreview.tsx
 * FILE_ID: UI-KANBAN-CARD-FACE-PREVIEW-001
 * PURPOSE: Static compact-card mock used in Board settings so the operator
 *          sees how the tile will look as they hide and reorder blocks.
 * WHY: Operator 2026-08-31 asked for a card image in settings, not a density
 *      toggle alone.
 * RELATED: ./cardFacePrefs.ts; ./CardFaceCustomizePanel.tsx.
 */
import type { ReactNode } from 'react'
import {
  Archive,
  CheckSquare,
  Clock,
  Flag,
  Grid3x3,
  MessageSquare,
  Move,
  Send,
  Target,
  UserPlus,
} from 'lucide-react'

import {
  COMPACT_CARD_FACE_BLOCKS,
  orderedVisibleCardFaceBlocks,
  type CardFaceBlockId,
  type KanbanCardFacePrefs,
} from './cardFacePrefs'

export interface CardFacePreviewProps {
  prefs: KanbanCardFacePrefs
}

function PreviewBlock({ id, children }: { id: CardFaceBlockId; children: ReactNode }) {
  return (
    <div data-face-block={id} className="min-w-0">
      {children}
    </div>
  )
}

const ACTION_ICONS = [Flag, Clock, Target, UserPlus, Grid3x3, Move, Send, Archive]

export function CardFacePreview({ prefs }: CardFacePreviewProps) {
  const blocks: Record<CardFaceBlockId, ReactNode> = {
    labels: (
      <PreviewBlock id="labels">
        <div className="mb-1 flex gap-0.5">
          <span className="h-1 w-8 rounded-sm bg-sky-400" />
          <span className="h-1 w-6 rounded-sm bg-amber-400" />
        </div>
      </PreviewBlock>
    ),
    title: (
      <PreviewBlock id="title">
        <div className="flex items-start gap-1.5">
          <span className="mt-0.5 h-3 w-3 shrink-0 rounded-full border border-neutral-300" />
          <p className="truncate text-[12px] font-medium text-neutral-800 dark:text-zinc-100">
            Quote #13652 — New Quote
          </p>
        </div>
      </PreviewBlock>
    ),
    actionLine: (
      <PreviewBlock id="actionLine">
        <p className="text-[10px] text-neutral-500">Next: send quote</p>
      </PreviewBlock>
    ),
    stateStrip: (
      <PreviewBlock id="stateStrip">
        <p className="rounded bg-amber-50 px-1 text-[10px] text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
          Blocked
        </p>
      </PreviewBlock>
    ),
    emailMeta: (
      <PreviewBlock id="emailMeta">
        <p className="truncate text-[10px] text-neutral-500">From accounts@…</p>
      </PreviewBlock>
    ),
    meta: (
      <PreviewBlock id="meta">
        <div className="flex items-center justify-between gap-2 text-[10px] text-neutral-500">
          <span className="inline-flex items-center gap-1">
            <CheckSquare className="h-3 w-3" />
            Checklist
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="rounded-full bg-neutral-100 px-1.5 py-0.5 text-[9px] font-semibold text-neutral-600 dark:bg-zinc-800 dark:text-zinc-300">
              Q-13652
            </span>
            <span className="grid h-4 w-4 place-items-center rounded-full bg-emerald-600 text-[8px] font-bold text-white">
              CH
            </span>
          </span>
        </div>
      </PreviewBlock>
    ),
    quoteFacts: (
      <PreviewBlock id="quoteFacts">
        <div className="flex items-baseline justify-between">
          <span className="text-[15px] font-bold tabular-nums text-neutral-400">$0.00</span>
          <span className="inline-flex items-center gap-0.5 text-[10px] text-neutral-500">
            <Clock className="h-3 w-3" />
            2d
          </span>
        </div>
      </PreviewBlock>
    ),
    actionBar: (
      <PreviewBlock id="actionBar">
        <div className="flex items-center gap-1 overflow-hidden rounded-md border border-neutral-200 px-1.5 py-1 dark:border-zinc-700">
          {ACTION_ICONS.map((Icon, index) => (
            <Icon key={index} className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
          ))}
        </div>
      </PreviewBlock>
    ),
    capture: (
      <PreviewBlock id="capture">
        <div className="flex items-center gap-1.5">
          <span className="min-w-0 flex-1 truncate rounded border border-neutral-200 px-1.5 py-0.5 text-[10px] text-neutral-400 dark:border-zinc-700">
            Type a note…
          </span>
          <span className="text-neutral-400">…</span>
          <span className="inline-flex items-center gap-0.5 rounded border border-violet-400 px-1.5 py-0.5 text-[10px] font-semibold text-violet-600">
            <MessageSquare className="h-3 w-3" />
            Thread
          </span>
        </div>
      </PreviewBlock>
    ),
  }

  return (
    <div
      data-testid="card-face-preview"
      className="rounded-xl border border-neutral-200 bg-white p-2 shadow-sm dark:border-zinc-700 dark:bg-zinc-900"
    >
      <div className="space-y-1">
        {orderedVisibleCardFaceBlocks(prefs)
          .filter((id) => COMPACT_CARD_FACE_BLOCKS.includes(id))
          .map((id) => (
            <div key={id}>{blocks[id]}</div>
          ))}
      </div>
    </div>
  )
}
